'use strict';

/**
 * RegistrationModule — merchant self-registration and its supporting public
 * lookups, called by an onboarding *vendor's* own credentials — same shape
 * as every other module: clientId/key authenticate and encrypt the call
 * through fintech-gateway's normal pipeline. Mirrors webpayment's
 * composables/apis/useUsersApi.ts#registor,
 * useOnboadingApi.ts#{MCCSearch,pincodeSearch,businessType,businessTurnOver}
 * and useSetupServices.ts#getPublicServiceCatalog.
 *
 * `/register` itself needs no credentials (a prospective merchant has
 * none yet), but when the caller *does* authenticate with a vendor's
 * clientId/key — the normal way to use this module — paymentSystem's
 * preHandler hook still resolves that vendor from the forwarded X-api-Key
 * and links the new merchant to it automatically (server.js's
 * OPTIONAL_AUTH_PATHS); nothing extra needs to be passed for that.
 */
class RegistrationModule {
  constructor(client) {
    this._c = client;
  }

  _post(path, body = {}) { return this._c.post(path, body); }
  _get(path, body = {})  { return this._c.get(path, body); }

  /**
   * Creates the merchant's login user + Merchant record. Auto-generates an
   * 8-char password server-side when none is given. Returns
   * { statusCode, message, data: { user, merchant, mid, existing? } } —
   * `data.existing: true` means a matching, not-yet-onboarded merchant
   * already exists and the caller should resume rather than re-register.
   * @param {object} payload - see webpayment's register/index.vue#submit for the full field shape
   */
  register(payload)     { return this._post('register', payload); }

  /** Service catalog for a "select services" step — each entry's
   *  `interfaces` list omits admin-only fields (no apiKey, etc).
   *  @returns {Promise<{statusCode, data: {id, service, interfaces: {id, interface}[]}[]}>} */
  getServiceCatalog()   { return this._get('setups/services/public-catalog'); }

  /** @returns {Promise<{statusCode, data: {id, label, value, status}[]}>} */
  getBusinessTypes()    { return this._get('admin/onboarding/businessType'); }

  /** @returns {Promise<{statusCode, data: {id, value}[]}>} */
  getTurnoverRanges()   { return this._get('admin/onboarding/trunOver'); }

  /** @param {string} q - server ignores queries shorter than 2 chars
   *  @returns {Promise<{statusCode, data: {id, descr}[]}>} */
  searchMcc(q)           { return this._get('admin/onboarding/search/mcc', { q }); }

  /** @param {string} q - server returns {statusCode:'01', data:[]} for queries shorter than 2 chars
   *  @returns {Promise<{statusCode, data: {id, pincode, city, district, statename}[]}>} */
  searchPincode(q)       { return this._get('admin/onboarding/search/pincode', { q }); }
}

module.exports = { RegistrationModule };
